'use client';

import React, { useState, useTransition } from 'react';
import Button from '@/ui/Button';
import { useFormState } from 'react-dom';
import { useRouter } from 'next/navigation';
import posthog from 'posthog-js';
import LoginFooter from '@/components/ui/Footer';
import { authenticateCredentials, authenticateSSO } from '../lib/actions';
import { FormField } from '../register/FormField';

export default function SignIn() {
	const [isSSO, setIsSSO] = useState<boolean | null>(null);
	const [isChecking, setIsChecking] = useState<boolean | null>(null);
	const [isPending, startTransition] = useTransition();
	const [error, formAction] = useFormState(isSSO === false ? authenticateCredentials : authenticateSSO, undefined);
	const router = useRouter();

	const handleForgotPassword = () => {
		posthog.capture('forgot_password_clicked');
		router.push('/login/forgot-password');
	};

	async function authenticateFormData(formData: FormData) {
		if (isSSO === null) {
			setIsChecking(true);
			const email = formData.get('email')?.toString();

			if (!email || !email.includes('@')) {
				setIsChecking(false);
				return;
			}

			const domain = email.split('@')[1];
			posthog.identify(email, { organization: domain });
			posthog.capture('sign_in_sso');

			try {
				const response = await fetch(`/api/check-sso?domain=${domain}`);
				const data = await response.json();
				setIsSSO(data.hasSSO);

				if (data.hasSSO) {
					formData.append('ssoEmailDomain', domain);
					formData.append('organizationId', data.organizationId);
					startTransition(() => {
						formAction(formData);
					});
				} else {
					console.log('SSO check response has no sso:', data);
				}
			} catch (e) {
				console.error(e);
				console.error('Error checking SSO:', e);
				setIsSSO(false);
			} finally {
				setIsChecking(false);
			}
		} else if (isSSO === false) {
			startTransition(() => {
				console.log('isSSO false:', formData);
				formAction(formData);
			});
		}
	}

	return (
		<>
			<form action={authenticateFormData} className="mx-auto flex w-[480px] flex-1 flex-col justify-center space-y-6">
				<div className="mb-2 text-5xl font-extrabold text-grey-900">Sign in</div>
				<div>
					<FormField label="Work Email" id="email" name="email" type="email" error={error} testId="work-email-input" />

					{/* only show password field for non-SSO */}
					{isSSO === false && !isChecking && (
						<div className="mt-4 flex flex-col">
							<FormField
								label="Password"
								id="password"
								name="password"
								type="password"
								error={error}
								testId="password-input"
							/>

							<button
								type="button"
								onClick={handleForgotPassword}
								className="mt-1 flex self-end text-button text-grey-900"
							>
								Forgot Password?
							</button>
						</div>
					)}
				</div>

				<Button
					isSubmit
					variant="contained"
					environment="light"
					disabled={isChecking || isPending}
					classOverrides={`!bg-primary-dark !text-grey-50 h-7 rounded-sm w-full hover:bg-secondary-dark hover:text-grey-50 ${
						isChecking || isPending ? 'opacity-90 animate-pulse' : ''
					}`}
				>
					{isPending ? 'Signing in...' : 'Continue'}
				</Button>
			</form>
			<LoginFooter />
		</>
	);
}
